import { ReturnOrder, Validation, Vehicle, VehicleAssessment } from "../../../system/Domain";
import { FinalInvoiceMetadata } from "../RemarketingLayout";
import moment from "moment/moment";
import dateFormats from "../../../system/dateFormats";
import Tile from "../../../Components/Tiles/Tile";
import TileContent from "../../../Components/Tiles/TileContent";
import { Box, FormControlLabel, Grid, Radio, RadioGroup, TextField } from "@mui/material";
import FormElement from "../../../Components/FormElement";
import SplitLabel from "../../../Components/SplitLabel";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

interface Props {
	returnOrder?: ReturnOrder
	metaData?: FinalInvoiceMetadata
	vehicle: Vehicle
	vehicleAssessment: VehicleAssessment
	validation: Validation
	setValidation: Dispatch<SetStateAction<Validation>>
	disabled: boolean
}

interface RadioNumberChoice {
	lineLabel: string
	buttonLabel: string
	value: number
}

interface RadioStringChoice {
	lineLabel: string
	buttonLabel: string
	value: string
}

interface FormRadioGroupLabelProps {
	choice: RadioNumberChoice | RadioStringChoice
}

const FormRadioGroupLabel = (props: FormRadioGroupLabelProps) => {
	return (
		<SplitLabel
			left={
				<Box>
					{props.choice.buttonLabel}
				</Box>
			}
			right={
				<Box>
					{props.choice.lineLabel}
				</Box>
			}
		/>
	);
};

const FinalInvoiceTile = (props: Props) => {
	const [customMileage, setCustomMileage] = useState<number>(0);
	const [customContractEndDate, setCustomContractEndDate] = useState<string>(null);

	// Calculate default vetoDays based on businessBranch
	const businessBranch = props.vehicle?.remarketing?.leasmanContract?.businessBranch;
	const defaultVetoDays =
		businessBranch?.key === "0147" ? 3 :
			businessBranch?.key === "0148" ? 5 :
				0;

	useEffect(() => {
		if (props.validation.vetoDays == null)
			props.setValidation(prevValidation => ({ ...prevValidation, vetoDays: defaultVetoDays }));
	}, [defaultVetoDays, props.validation.vetoDays]);

	const primaryRnp = props.metaData?.returnForms?.filter(f => f.isFirst);
	const milageChoices = useMemo((): RadioNumberChoice[] => {
		const result = [];

		if (primaryRnp && primaryRnp.length > 0) {
			result.push({
				lineLabel: "Rücknahmeprotokoll",
				buttonLabel: primaryRnp[0].mileage.toString(),
				value: primaryRnp[0].mileage
			});
		}

		if (props.vehicleAssessment && props.vehicleAssessment.mileage) {
			result.push({
				lineLabel: "Gutachten",
				buttonLabel: props.vehicleAssessment.mileage.toString(),
				value: props.vehicleAssessment.mileage
			});
		}

		return result;
	}, [primaryRnp, props.vehicleAssessment]);

	const contractEndDateChoices = useMemo((): RadioStringChoice[] => {
		const result = [];

		if (props.metaData?.initialCollectionAppointment?.notBefore) {
			result.push({
				lineLabel: "Rückholung Kundenwunschtermin",
				buttonLabel: moment(props.metaData?.initialCollectionAppointment?.notBefore).format(dateFormats.date),
				value: moment(props.metaData?.initialCollectionAppointment?.notBefore).toISOString(false)
			});
		}
		if (props.vehicleAssessment?.assessmentDate) {
			result.push({
				lineLabel: "Besichtigungsdatum (Gutachter)",
				buttonLabel: moment(props.vehicleAssessment.assessmentDate).format(dateFormats.date),
				value: moment(props.vehicleAssessment.assessmentDate).toISOString(false)
			});
		}
		if (props.vehicle.remarketing.leasmanContract?.dateEnd) {
			result.push({
				lineLabel: "kalkuliertes Vertragsende",
				buttonLabel: moment(props.vehicle.remarketing.leasmanContract?.dateEnd).format(dateFormats.date),
				value: moment(props.vehicle.remarketing.leasmanContract?.dateEnd).toISOString(false)
			});
		}
		if (primaryRnp && primaryRnp.length > 0 && primaryRnp[0].dateCompleted) {
			result.push({
				lineLabel: "Rücknahmeprotokoll",
				buttonLabel: moment(primaryRnp[0].dateCompleted).format(dateFormats.date),
				value: moment(primaryRnp[0].dateCompleted).toISOString(false)
			});
		}
		if (props.vehicle.remarketing.arrival?.dateCompleted) {
			result.push({
				lineLabel: "Eingangsdatum Gebrauchtwagenplatz",
				buttonLabel: moment(props.vehicle.remarketing.arrival?.dateCompleted).format(dateFormats.date),
				value: moment(props.vehicle.remarketing.arrival?.dateCompleted).toISOString(false)
			});
		}

		return result;
	}, [props.metaData, props.vehicle, props.vehicleAssessment]);

	useEffect(() => {
		if (!props.vehicle || !props.validation) return;

		if (props.validation.mileage) {
			const hasMatch = milageChoices.some(c => c.value === props.validation.mileage);

			if (!hasMatch) setCustomMileage(props.validation.mileage);
		}

		if (props.validation.contractEndDate) {
			const hasMatch = contractEndDateChoices.some(c => c.value === props.validation.contractEndDate);

			if (!hasMatch) setCustomContractEndDate(props.validation.contractEndDate);
		}

	}, [props.vehicle, props.validation]);

	return (
		<Tile title="Endabrechnung">
			<TileContent dense>
				<Box p={2} pb={4}>
					<Grid container spacing={3}>
						<FormElement label="Rückgabekilometer">
							<RadioGroup
								value={props.validation.mileage}
								onChange={(e) => {
									let selectedValue = parseInt(e.target.value);
									if (customMileage && selectedValue === -1)
										selectedValue = customMileage;

									props.setValidation(prevValidation => ({ ...prevValidation, mileage: selectedValue }));
								}}
							>
								{milageChoices.map((choice, index) => (
									<FormControlLabel
										value={choice.value}
										key={index}
										control={<Radio />}
										label={<FormRadioGroupLabel choice={choice} />}
										sx={{
											width: "100%",
											"& .MuiFormControlLabel-label": {
												width: "100%"
											}
										}}
										disabled={props.disabled}
									/>
								))}
								<FormControlLabel
									control={<Radio />}
									value={customMileage}
									disabled={props.disabled}
									label={
										<SplitLabel
											left={
												<TextField
													type="number"
													label=""
													variant="standard"
													value={customMileage}
													onChange={(e) => {
														let selectedValue = parseInt(e.target.value);
														setCustomMileage(selectedValue);
														props.setValidation(prevValidation => ({ ...prevValidation, mileage: selectedValue }));
													}}
													disabled={props.disabled}
												/>
											}
											right={
												<Box fontStyle="italic" whiteSpace="nowrap">
													Freie Eingabe
												</Box>
											}
										/>
									}
									sx={{
										width: "100%",
										"& .MuiFormControlLabel-label": {
											width: "100%"
										}
									}}
								/>
							</RadioGroup>
						</FormElement>

						<FormElement label="Beendigungsdatum">
							<RadioGroup
								value={props.validation.contractEndDate}
								onChange={(e) => {
									let selectedValue: any = e.target.value;
									if (selectedValue === false) {
										selectedValue = customContractEndDate;
									}

									props.setValidation(prevValidation => ({ ...prevValidation, contractEndDate: selectedValue }));
								}}
							>
								{contractEndDateChoices.map((choice, index) => (
									<FormControlLabel
										value={choice.value}
										key={index}
										control={<Radio />}
										label={<FormRadioGroupLabel choice={choice} />}
										sx={{
											width: "100%",
											"& .MuiFormControlLabel-label": {
												width: "100%"
											}
										}}
										disabled={props.disabled}
									/>
								))}
								<FormControlLabel
									control={<Radio />}
									disabled={props.disabled}
									value={customContractEndDate || ""}
									label={
										<SplitLabel
											left={
												<DatePicker
													format="DD.MM.YYYY"
													label=""
													disabled={props.disabled}
													slotProps={{
														textField: {
															variant: 'standard',
															disabled: true,
															error: false, // suppress error display
															sx: {
																...(!props.disabled && {
																	"& input.Mui-disabled": {
																		WebkitTextFillColor: "white"
																	}
																})
															}
														}
													}}
													value={moment(customContractEndDate)}
													onChange={(d: any) => {
														const selectedValue = (d || moment()).startOf("d").toISOString(true);

														setCustomContractEndDate(selectedValue);
														props.setValidation(prevValidation => ({ ...prevValidation, contractEndDate: selectedValue }));
													}}
												/>
											}
											right={
												<Box fontStyle="italic" whiteSpace="nowrap">
													Freie Eingabe
												</Box>
											}
										/>
									}
									sx={{
										width: "100%",
										"& .MuiFormControlLabel-label": {
											width: "100%"
										}
									}}
								/>
							</RadioGroup>
						</FormElement>

						<FormElement label="Vetofrist">
							<RadioGroup
								value={props.validation.vetoDays ?? defaultVetoDays}
								onChange={(e) => {
									const selectedValue = parseInt(e.target.value);
									props.setValidation(prevValidation => ({ ...prevValidation, vetoDays: selectedValue }));
								}}
							>
								{[
									{ label: "0 Tage", value: 0 },
									{ label: "3 Tage", value: 3 },
									{ label: "5 Tage", value: 5 }
								].map((choice, index) => (
									<FormControlLabel
										value={choice.value}
										key={index}
										control={<Radio />}
										label={choice.label}
										sx={{
											width: "100%",
											"& .MuiFormControlLabel-label": {
												width: "100%"
											}
										}}
										disabled={props.disabled}
									/>
								))}
							</RadioGroup>
						</FormElement>

						<Box sx={{ width: "100%", display: "flex", flexDirection: "row", paddingLeft: 3, paddingTop: 3 }}>
							<TextField
								label="Rückholkosten z.L. des Kunden"
								disabled={!!props.returnOrder}
								variant="standard"
								fullWidth
								InputLabelProps={{ shrink: true }}
								value={
									props.returnOrder?.costTransferCustomer
										? `Rückholung: ${props.returnOrder.costTransferCustomer}`
										: 'keine Angabe'
								}
							/>
						</Box>

						<Box sx={{ width: "100%", display: "flex", flexDirection: "row", paddingLeft: 3, paddingTop: 2 }}>
							<TextField
								label="Rückholkosten z.L. von Ayvens"
								disabled={!!props.returnOrder}
								variant="standard"
								fullWidth
								InputLabelProps={{ shrink: true }}
								value={
									props.returnOrder?.debitingAld
										? `Rückholung: ${props.returnOrder.debitingAld}`
										: 'keine Angabe'
								}
							/>
						</Box>

						<Box sx={{ width: "100%", display: "flex", flexDirection: "row", paddingLeft: 3, paddingTop: 2 }}>
							<TextField
								label="Rückholkosten z.L. der Rate"
								disabled={!!props.returnOrder}
								variant="standard"
								fullWidth
								InputLabelProps={{ shrink: true }}
								value={
									props.returnOrder?.transportCostInstallments
										? `Rückholung: ${props.returnOrder.transportCostInstallments}`
										: 'keine Angabe'
								}
							/>
						</Box>

						<Box sx={{ width: "100%", display: "flex", flexDirection: "row", paddingLeft: 3, paddingTop: 2 }}>
							<TextField
								label="Kundenverschuldete Zusatzkosten"
								disabled={!!props.returnOrder}
								variant="standard"
								fullWidth
								InputLabelProps={{ shrink: true }}
								value={
									props.returnOrder?.carryoverCost
										? `Rückholung: ${props.returnOrder.carryoverCost}`
										: 'keine Angabe'
								}
							/>
						</Box>
					</Grid>
				</Box>
			</TileContent>
		</Tile>
	);
};

export default FinalInvoiceTile;
